import { useState, useRef, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { MoonLoader, BounceLoader } from 'react-spinners';

import { UserContext } from 'contexts/user';
import { ReactComponent as CloseStrokeSVG } from 'icons/close-stroke.svg';
import Select from 'components/select/select';
import Upload from './upload';
import Checkbox from 'components/checkbox/checkbox';

import { TYPES, DOCTOR_TYPES, TECHNICIAN_TYPES } from 'config/document';

import { useOutsideClick } from 'helpers/clickOutside';

const DocumentUploadModal = ({
  fileUploadProgress,
  fileUploadProgressLabel,
  onClose,
  onUpload,
  onClickOutside,
}) => {
  const { user } = useContext(UserContext);
  const modalRef = useRef(null);

  useOutsideClick(modalRef, onClickOutside);

  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [type, setType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState(null);
  const [shared, setShared] = useState(false);
  const [validation, setValidation] = useState({
    type: true,
    files: true,
  });

  const checkValidity = useCallback(() => {
    const validation = {
      type: !!type,
      files: !!files,
    };

    setValidation(validation);

    return Object.values(validation).every(Boolean);
  }, [type, files]);

  const onSelectChange = (selectedOption) => {
    setType(selectedOption);
  };

  const onCheckboxChange = () => {
    setShared(!shared);
  };

  const handleUpload = async () => {
    if (checkValidity()) {
      setUploading(true);
      await onUpload(type, files, shared);
      setUploading(false);
    }
  };

  return (
    <div
      className="fullscreen-modal-container"
      style={{ visibility: 'visible' }}
    >
      <div className="fullscreen-modal-background"></div>
      <div className="fullscreen-modal" ref={modalRef}>
        <section className="modal-content">
          <header className="modal-header">
            <h1>Document upload</h1>
          </header>
          <span onClick={onClose} className="pointer close">
            <CloseStrokeSVG />
          </span>

          <div className="modal-body overflow-auto">
            <div className="flex flex-column items-center pt8">
              <div className="w-100 flex flex-row items-start items-center">
                <Select
                  valid={validation.type}
                  validationText="This question is required!"
                  isMulti={false}
                  label="Document type*"
                  value={type}
                  options={
                    user.role === 'doctor'
                      ? DOCTOR_TYPES
                      : user.role === 'technician'
                      ? TECHNICIAN_TYPES
                      : TYPES
                  }
                  onChange={onSelectChange}
                />
              </div>

              <div className="input-group w-100">
                <label>Patient&apos;s access</label>
                <Checkbox
                  name="shared"
                  label="Shared with patient"
                  checked={shared}
                  valid={true}
                  onChange={onCheckboxChange}
                />
              </div>
              <div className="w-100">
                {uploading ? (
                  <div
                    className="flex flex-column justify-center items-center"
                    style={{ height: '198px' }}
                  >
                    <div
                      className="mb5 midgrey-d1 tc"
                      dangerouslySetInnerHTML={{
                        __html: fileUploadProgressLabel,
                      }}
                    />
                    <MoonLoader color="#455d7a" size={60} />

                    <h1 className="mt5 midgrey-d1">{fileUploadProgress}%</h1>
                  </div>
                ) : (
                  <Upload
                    name="avatar"
                    label="File*"
                    accept="file"
                    valid={validation.files}
                    onFileUpload={(files) => {
                      setFiles(files);
                      setIsLoadingFiles(true);
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer flex justify-between">
            <div className="flex th-post-header-secondary-actions th-btn-group left"></div>
            <div>
              <button
                className="flex items-center btn-green"
                disabled={uploading}
                onClick={() => handleUpload()}
              >
                {isLoadingFiles && !files && (
                  <BounceLoader size={15} color="#a7a6ad" />
                )}
                <span className="ml2">Upload</span>
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

DocumentUploadModal.propTypes = {
  fileUploadProgress: PropTypes.number,
  fileUploadProgressLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
};

export default DocumentUploadModal;
